import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppPagination from "@toothfairy/shared-ui/AppPagination";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import entitiesHelper from "../../../../API/EntitiesAI/entitiesHelper";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import ToothFairyModal from "../../../../Components/ToothFairyComponents/ToothFairyModal";
import { isLargeScreen } from "../../../MainViewContainer";
import SettingsAI from "../SettingsAI";
import NERsSettingsToolbar from "./NERsSettingsToolbar";

const NERsList = () => {
  const formRef = useRef();
  const { currentTheme } = AppTheme.useTheme();
  const { height } = useWindowDimensions();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const [showNERModal, setShowNERModal] = React.useState(false);
  const [showChoice, setShowChoice] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [isLoading] = React.useState(false);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const pageSize = 50;
  const [displayedNERs, setDisplayedNERs] = React.useState([]);
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const [initialMapValues, setInitialMapValues] = useState({
    label: "",
    description: "",
    emoji: "",
  });

  const columns = [
    {
      title: "ID",
      width: 0.00001,
    },
    {
      title: "Emoji",
      width: 0.01,
    },
    {
      title: "Label",
      width: 0.3,
    },
    {
      title: "Description",
    },
    {
      title: "Actions",
      isAction: true,
      width: 0.1,
    },
  ];

  const ners = useMemo(() => {
    return state?.aiEntitiesConfig?.filter((i) => i.type === "ner") || [];
  }, [state?.aiEntitiesConfig]);

  useEffect(() => {
    const sortedNERs = [...ners].sort((a, b) =>
      a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
    );
    setDisplayedNERs(sortedNERs);
  }, [ners]);

  const onNERCreated = useCallback(
    (event) => {
      if (event === "success") {
        AppToasty.show(
          t(
            isEditMode ? "NER updated successfully" : "NER created successfully"
          ),
          {
            type: "success",
          }
        );
      } else {
        AppToasty.show(
          t(isEditMode ? "NER update failed" : "NER creation failed"),
          {
            type: "danger",
          }
        );
      }
      formRef?.current?.resetForm();
      setShowNERModal(false);
      setSelectedIndex(null);
      setIsEditMode(false);
    },
    [AppToasty, t, isEditMode]
  );

  const nerItemCreationComponent = useMemo(() => {
    return (
      <ToothFairyModal
        modalStyle={{
          maxHeight: isLargeScreen() ? "95%" : 500,
          height: "auto",
          maxWidth: isLargeScreen() ? 700 : 350,
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          margin: "auto",
        }}
        key={(Math.floor(Date.now() / 5000) * 5000).toString()}
        isModalOpen={showNERModal}
        setIsModalOpen={setShowNERModal}
        modalTitle={isEditMode ? "Edit NER" : "Create NER"}
        closeModal={() => {
          formRef?.current?.resetForm();
          setShowNERModal(false);
          setSelectedIndex(null);
        }}
      >
        <SettingsAI
          ref={formRef}
          initialValues={initialMapValues}
          onEntityCreated={onNERCreated}
          isEdit={isEditMode}
          settingType="ner"
          sectionTitle={isEditMode ? initialMapValues.label : "New NER"}
          opt={isEditMode ? displayedNERs[selectedIndex] : null}
        />
      </ToothFairyModal>
    );
  }, [
    showNERModal,
    setShowNERModal,
    JSON.stringify(initialMapValues),
    isEditMode,
    selectedIndex,
    displayedNERs,
    displayedNERs[selectedIndex]?.id,
  ]);

  const identifyOptByID = (id) => {
    return displayedNERs?.find((u) => u.id === id);
  };

  const handleNERItemSelection = ({ id, index }) => {
    setSelectedIndex(index);
    setIsEditMode(true);
    let extendedItem = identifyOptByID(id);
    setInitialMapValues({
      ...extendedItem,
    });
    setShowNERModal(true);
  };

  const handleNERItemDeletion = async () => {
    try {
      setShowChoice(false);
      if (selectedIndex !== null) {
        const _id = displayedNERs[selectedIndex]?.id;
        await entitiesHelper.deleteEntity(_id);

        const updatedEntities = state?.aiEntitiesConfig?.filter(
          (u) => u.id !== _id
        );
        dispatch("SET_AI_ENTITIES_CONFIG", updatedEntities);

        AppToasty.show(t("NER deleted successfully"), {
          type: "success",
        });
      } else {
        throw new Error("No item selected");
      }
    } catch (error) {
      console.log(error);
      AppToasty.show(t("NER deletion failed"), {
        type: "danger",
      });
    }
  };

  const handleSearch = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim() === "") {
      const sortedNERs = [...ners].sort((a, b) =>
        a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
      );
      setDisplayedNERs(sortedNERs);
      return;
    }

    const filteredItems = ners.filter((item) => {
      return (
        item.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
    setDisplayedNERs(filteredItems);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleCreation = () => {
    setInitialMapValues({
      label: "",
      description: "",
      emoji: "",
    });
    setIsEditMode(false);
    setShowNERModal(true);
  };

  const isItemActionable = () => {
    return true;
  };

  const _coreChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={"Cancel"}
        caption="Delete NER"
        subCaption="Are you sure you want to delete this NER?"
        confirmCaption={"Delete"}
        onCancelButtonPress={() => setShowChoice(false)}
        onConfirmButtonPress={() => handleNERItemDeletion()}
        setShowChoice={setShowChoice}
        showChoice={showChoice}
      />
    );
  }, [setShowChoice, showChoice, selectedItem, setSelectedItem]);

  return (
    <View
      style={{
        minWidth: "100%",
        paddingLeft: 10,
        flex: 1,
        overflow: isLoading && "hidden",
      }}
    >
      <NERsSettingsToolbar
        handleSearch={handleSearch}
        handleCreation={handleCreation}
      />
      {isLoading ? (
        <ToothFairyLoadingPlaceholder
          backgroundColor={currentTheme?.theme?.softBackgroundColor}
          visible={true}
          containerStyle={{
            backgroundColor: currentTheme?.theme?.transparent,
            minHeight: 250,
          }}
          caption="Loading NERs..."
        />
      ) : (
        <>
          {displayedNERs.length > 0 ? (
            <AppTable
              actionButtons
              mapIconWithStatus={() => {
                return AppAssets.icons.activeModel;
              }}
              mainActionLabel="View"
              showDropDownMenuForActions
              onClickMainAction={(item, index) => {
                handleNERItemSelection({
                  id: item?.id,
                  index: index,
                });
              }}
              actionButtonStyle={{
                minWidth: 20,
              }}
              onActionItemPress={async (id, item, index) => {
                if (id === "EDIT") {
                  handleNERItemSelection({
                    id: item?.id,
                    index: index,
                  });
                } else if (id === "DELETE") {
                  setSelectedItem(item);
                  setSelectedIndex(index);
                  setShowChoice(true);
                }
              }}
              actions={[
                {
                  label: "Edit",
                  id: "EDIT",
                  icon: AppAssets.icons.edit,
                },
                {
                  label: "Delete",
                  id: "DELETE",
                  icon: AppAssets.icons.delete,
                },
              ]}
              isItemActionable={() => isItemActionable()}
              style={{
                zIndex: -1,
                height: height - 120,
                overflowY: "auto",
                overflowX: "hidden",
              }}
              headers={columns}
              items={displayedNERs
                ?.slice(
                  (currentPage - 1) * pageSize,
                  (currentPage - 1) * pageSize + pageSize
                )
                ?.map((u) => {
                  return {
                    id: u?.id || " ",
                    emoji: u?.emoji || " ",
                    label: u?.label || " ",
                    description: u?.description || "No description provided",
                    actions: " ",
                  };
                })}
            />
          ) : (
            <AppPlaceholderView
              wrapperStyle={{
                width: "100%",
                height: "100%",
                marginVertical: 20,
                marginHorizontal: "auto",
                flex: 1,
              }}
              size={30}
              icon={AppAssets.icons.orbit}
              mainCaption="EmptyPlaceholder"
              subCaption="No NERs found"
            />
          )}
          {displayedNERs?.length > pageSize && (
            <AppPagination
              onPageChange={handlePageChange}
              totalCount={displayedNERs?.length}
              siblingCount={1}
              currentPage={currentPage}
              pageSize={pageSize}
              wrapperStyle={{
                marginTop: 10,
                alignSelf: "flex-end",
              }}
            />
          )}
        </>
      )}
      {nerItemCreationComponent}
      {_coreChoice}
    </View>
  );
};

export default NERsList;
